<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when user (as an authenticated admin) selects the 
VIEW ASSETS route on the initial splash page.  Assets are listed with significant properties in a table row format
if the @media probe returns a screen size > MOBILE.  If MOBILE size is detected, then agreement 'rows' are rendered in  a box 
container with alternating label:value rows in a single column.   Actions are rendered as buttons - three to a column.

Actions available on this component for authorized users (Admins) are {DELETE (asset)} 

Revisions:
    02/25/2025      pblucas     initial creation


-->
<template>
    <div class="_master_container">

        <!-- spin until data if loaded from local file -->
        <div v-if="(renderState == 'LIST') || (renderState == 'DISPLAY-IMPORT-STATS')">
            <div v-if="!bAssetsLoaded">
                <p class="_p_patience"> Loading assets from database - be patient!</p>
                <SemipolarSpinner class="_spinner"
                :animation-duration="2000"
                :size="65"
                color="#13294B"/>
            </div>
        </div>

        <div v-if="renderState =='LIST'" >
            <div v-if="filteredAssets.length > 0">
                <div class="_list">
                    <p class="_p_title_header">
                        Organizational Assets for: {{ orgDisplay }} <br />
                        <span style="font-weight: normal;">Number of assets: {{ assetCount}} <br />
                        <b>Note:</b> Due to large number of assets, filters require <br />
                        loss-of-focus or a return to activate.</span>
                    </p>
                </div>
                <div class="_scrollable_list_container">
                    <div class="_assets_table">
                        <!-- asset filter entry -->
                        <div class="_assets_table_fixed_row" style="top: 0;">
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['tag']" @keyup="setFilters()">
                            </div>
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['asset-type']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['owner-org']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['steward']" @keyup="setFilters()">
                            </div>
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['steward-netid']" @keyup="setFilters()">
                            </div>
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['status']" @keyup="setFilters()">
                            </div>
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['serial-no']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['description']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['mfg']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['model']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <input class="_input_filter" type="text" v-model.lazy="filters['location']" @keyup="setFilters()">
                            </div> 
                            <div class="_assets_table_cell">
                                <button class="_button_clear_filters" @click="clearFilters">Clear Filters</button>
                            </div> 

                        </div>
                        <!-- asset table headers -->
                        <div class="_assets_table_fixed_row" style="top: 25px;">
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Tag</p>
                            </div> 
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Asset Type</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Owner Org</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Steward</p>
                            </div> 
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Steward ID</p>
                            </div> 
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Asset Status</p>
                            </div> 
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Serial No</p>
                            </div> 
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Description</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Manufacturer</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Model</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_header">Location</p>
                            </div>
                            <div class="_assets_table_cell">
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- asset property values -->
                        <div class="_assets_table_row" v-for="(asset,idx) in filteredAssets" :key="filteredAssets[idx].uuid">
                            <div class="_assets_table_link_cell" 
                                tabindex="0"
                                aria-label="Asset tag link to full asset detail sheet" 
                                aria-describedby="Allows user to select full asset fisplay page on tag link" 
                                @click="viewAsset(filteredAssets[idx], idx)"
                                @keydown.enter="viewAsset(filteredAssets[idx], idx)">
                                {{ filteredAssets[idx].tag }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ prettyType(filteredAssets[idx]['asset-type'])}}
                            </div>
                            <div class="_assets_table_cell" >
                                {{ filteredAssets[idx]['owner-org'] }}
                            </div>  
                            <div class="_assets_table_cell" >
                                {{ filteredAssets[idx]['steward'] }}
                            </div>              
                            <div class="_assets_table_cell" >
                                {{ filteredAssets[idx]['steward-netid'] }}
                            </div>              
                            <div class="_assets_table_cell" >
                                {{ filteredAssets[idx]['status'] }}
                            </div>              
                            <div class="_assets_table_cell" >
                                {{ filteredAssets[idx]['serial-no'] }}
                            </div>  
                            <div class="_assets_table_cell_ellipsis">
                                {{ filteredAssets[idx].description }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ filteredAssets[idx].mfg }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ filteredAssets[idx].model }}
                            </div>
                            <div class="_assets_table_cell">
                                {{ filteredAssets[idx]['location'] }}
                            </div>
                            <div class="_asset_actions_table_cell">
                                <button class="_action_button" style="order:2;" 
                                    tabindex="0" 
                                    aria-label="Edit asset in list row" 
                                    aria-describedby="Open asset full view for editing" 
                                    @click.stop="editAsset(filteredAssets[idx], idx)"
                                    @keydown.enter.stop="editAsset(filteredAssets[idx], idx)">Edit Asset</button>
                                <button class="_action_button" style="order:3;"
                                    tabindex="0" 
                                    aria-label="View asset in list row" 
                                    aria-describedby="Opens asset full view for review" 
                                    @click.stop="viewAsset(filteredAssets[idx], idx)"
                                    @keydown.enter.stop="viewAsset(filteredAssets[idx], idx)">View Asset</button>
                            </div>
                         </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
             <div class="_no_assets_warning_container" v-if="filteredAssets.length == 0 && bAssetsLoaded">
                <p class="_assets_warning" style="order: 1;">
                    There are no assets on the provided org list
                </p>
                <div class="_assets_warning_clear" style="order: 2;">
                    <button class="_main_button" 
                        tabindex="0"
                        aria-label="Clears filter list" 
                        aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                        @click="clearFilters"
                        @keydown.enter="clearFilters">Clear Filters</button>
                </div> 
            </div>      
        </div>      <!-- end of list render -->

        <div v-if="renderState =='VIEW'">
            <!-- render the ViewAssetEntire View on selected asset -->
            <ViewAssetEntire  :key="filteredAssets[viewIndex].uuid"
                              :asset="filteredAssets[viewIndex]"/>
                         
        </div>

        <!-- edit the selected asset -->
        <div v-if="renderState =='EDIT'">
            <div class="_saved_right" v-if="savedFlag">
                <p class="_p_saved">Asset Updated</p>
            </div>

            <EditAsset  :key="filteredAssets[viewIndex].uuid"
                        :asset="filteredAssets[viewIndex]" 
                        :restriction="'NONE'"/>
        </div>

        <div class="_assets_controls" v-if="renderState == 'LIST'">
            <button class="_main_button" type="button" 
                tabindex="0" 
                aria-label="Import assets from file" 
                aria-describedby="Allows user to import assets from  CSV, XLSX, FabWeb+ or JSON file"
                @click="this.renderState = 'IMPORT'"
                @keydown.enter="this.renderState = 'IMPORT'">Import Assets </button>
            <button class="_main_button" type="button" 
                tabindex="0" 
                aria-label="Export assets as JSON, XLSX, or CSV file" 
                aria-decribedby="Allows user to export and save assets as CSV, XLSX, or JSON file" 
                @click="exportAssets()"
                @keydown.enter="exportAssets()">Export Assets </button>    
        </div>

        <div v-if="renderState == 'IMPORT'">           
            <p class="_p_import">
                When any asset (from FabWeb, FabWeb+, or Other) is imported, an asset object will be created with an 
                unique UUID and id and stored in the database.  If the asset's tag is already on an asset in the database, 
                then any properties on the import will be used to update the database rather than create a new 
                asset.  That is, tags are taken to be unique and trigger an update on duplicate key, as will the UUID.
                <br /><br />
                'Other' imports can include exports from this application.  Such exports will have Tags and UUID set.  Additionally, 
                exports from this application will have header column names that have been validated and will ensure 
                that all properties will be properly imported.  It is recommended that an export of the existing assets  
                be performed first so as to generate a template for any subsequent imports.

                <br /><br />
                For FabWeb and FabWeb+ imports, the Excel file is generated against the unit's org code at a macro 
                level so this information is requested to ensure that thet assets are propertly assigned to the 
                correct organization.
            </p>
          
            <br />
            <div class="_assets_file_controls">
                <!-- <button class="_button_export_1"       type="button" @click="selectXLSXFile()">Import from Excel XLSX</button> -->
                <button class="_main_button"  type="button" 
                    tabindex="0" 
                    aria-label="Import from FabWeb+ file" 
                    aria-description="Selects XLSX file type for FabWeb+ asset import" 
                    @click="selectFabWebPlusFile()"
                    @keydown.enter="selectFabWebPlusFile()">Import from FabWeb+ XLSX </button>
                <button class="_main_button"  type="button" 
                    tabindex="0" 
                    aria-label="Import from FabWeb file" 
                    aria-description="Selects XLSX file type for FabWeb asset import" 
                    @click="selectFabWebFile()"
                    @keydown.enter="selectFabWebFile()">Import from FabWeb XLSX </button>
               <button class="_main_button"  type="button" 
                    tabindex="0" 
                    aria-label="Import from CSV, XLSX, or JSON file" 
                    aria-description="Selects CSV, SLXS, or JSON file type for asset import" 
                    @click="selectOtherFile()"
                    @keydown.enter="selectOtherFile()">Import from File Other than FabWeb/FabWeb+</button>
    
             </div>
        </div>
        <div v-if="renderState == 'FILE'">
            <div v-if="processSelector == 'FABWEB+'">
                <p class="_p_import">               
                    
                    With FabWeb+ selected, the specific file to be imported should have been generated 
                    from the Reports/General Reports selection.  On the FabWeb+ application, this will export 
                    all of the asset properties in the FabWeb+ database.   <br/><br/>
                    
                    Each allowed org for the user generates a separate sheet in the workbook.  The import will 
                    iterate all sheets to capture all the applicable org assignments.  On any specific worksheet, 
                    the first rows are headers and title information.  The column headers appear in the 6th row, and
                    data begins at the 7th row. All column headers are checked to ensure that the selected spreadsheet 
                    headers are in fact those of the FabWeb+ general report.  If not, the import will throw an error.
                    
                </p>
                <p class="_p_import">
                    Because FabWeb+ requires a custodian, unit assets are configured with unit member as the asset custodian.
                    The import mechanism utilizes a JSON configuration file which maps unit custodians to named units.  Where 
                    the assets are not typically specific to individuals (e.g. laptops/desktops), the asset custodian is altered to
                    be the named unit.   Thus, network switches are assigned to a 'networking' custodian rather that to a specific
                    individual.  Some manual adjustment may be necessary to edit the asset and assign the correct custodian.
                    <br /><br />
                    The category specified in the FabWeb+ file maps to the asset-type category in this system.  A JSON configuration 
                    file likewise controls the type aliases.
                    <br /><br />
                    <b>Note:</b>   Any non-FabWeb+ spreadsheet column labels must be in the API collection dictionary as class
                    properties in order for the values to be processed.  If not, values are skipped and not written 
                    to asset object.
                    <br/><br/>               
                </p>
            </div>
            <div v-if="processSelector=='FABWEB'">
                <p class="_p_import">               
                        When a FABWeb asset is imported, an asset object will be created with an unique UUID and id and 
                        stored in the database.  If the asset's tag is already on an asset in the database, 
                        then any properties on the import will be used to update the database rather than create a new 
                        asset.  That is, tags are taken to be unique and trigger an update on duplicate key.
                        <br/><br/>
        
                    </p>
            </div>

            <p class="_p_import_center"> 
                Select a file to import.  <br />
                <span v-if="processSelector == 'FABWEB+'">
                    <b>You are importing from FabWeb+.</b>
                </span> 
            </p>
            <div class="_file_entry">
                
                <input id="fileSpecifier" class="_file_selector" type="file" tabindex="-1" 
                    aria-label="File selector" 
                    aria-descsribedby="Selects filename from client machine to import" 
                    accept=".csv,.xls,.xlsx,text/csv,application/json, application/json, application/vnd: ms-excel"  
                    @change="importFileSelection"/>
            </div>
            <br/>
            <div class="_file_entry" v-if="bFileTypeError">
                <p class="_assets_warning">You have selected a file with an invalid file type.</p>

            </div>
        </div>
 
        <!-- spin until data if loaded from local file -->
        <div v-if="renderState == 'READ-FABWEB-DATA'">
            <div v-if="!bDataAvail">
                <p class="_p_patience">Importing from sheet {{ currentSheet }}</p>
                <SemipolarSpinner class="_spinner"
                :animation-duration="2000"
                :size="65"
                color="#13294B"/>
            </div>
        </div>

        <div v-if="renderState == 'DISPLAY-IMPORT-STATS' && bAssetsLoaded">
            
            <p class="_p_title_header">
                Import and Process Statistics
            </p>
            
            <div class="_stat_table">
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Imported File Name</p></div>
                    <div class="_stat_table_cell">{{ prettySelectedFilename }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Input Sheets Processed</p></div>
                    <div class="_stat_table_cell">{{ stats.sheetsProcessed }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Number of Import Records</p></div>
                    <div class="_stat_table_cell">{{ stats.inputRecords.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Number of Bad Orgcode Records</p></div>
                    <div class="_stat_table_cell">{{ stats.badOrgcodeRecords.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Number of Fin Records Removed</p></div>
                    <div class="_stat_table_cell">{{ stats.finRecords.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row_bordered">
                    <div class="_stat_table_cell"><p class="_p_list_header">Valid Source Records</p></div>
                    <div class="_stat_table_cell">{{ stats.validSourceRecords.toLocaleString() }}</div>
                </div>
                 <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Number of Bad Item-Id Records</p></div>
                    <div class="_stat_table_cell">{{ stats.badItemIDLengthRecords.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Blank or N/A Model Entries</p></div>
                    <div class="_stat_table_cell">{{ stats.modelInputEmpty.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Unresolved Mfg Entries</p></div>
                    <div class="_stat_table_cell">{{ stats.unresolvedMfgInputs.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Unresolved Model Entries</p></div>
                    <div class="_stat_table_cell">{{ stats.unresolvedModelInput.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row_bordered">
                    <div class="_stat_table_cell"><p class="_p_list_header">Model Inference Success Rate</p></div>
                    <div class="_stat_table_cell"> <b>{{ stats.modelEvalSuccessRate.toFixed(1) }}%</b></div>
                </div>   
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Records without Custodians</p></div>
                    <div class="_stat_table_cell">{{ stats.noCustodiansListed.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Custodians not in AD</p></div>
                    <div class="_stat_table_cell">{{stats.custodiansNotInAD.toLocaleString()  }}</div>
                </div>              
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Records with Types as Other</p></div>
                    <div class="_stat_table_cell">{{ stats.notListedTypeRecords.toLocaleString() }}</div>
                </div>
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Overall Evaluation Success Rate</p></div>
                    <div class="_stat_table_cell"> <b>{{ stats.inferenceSuccessRate.toFixed(1) }}%</b></div>
                </div>              
                <div class="_stat_table_row">
                    <div class="_stat_table_cell"><p class="_p_list_header">Input Processing Elapsed Time</p></div>
                    <div class="_stat_table_cell">{{ stats.elapsedImportTime }}</div>
                </div>
            </div>       
            <br />
       </div>

        <div class="_export_container" v-if="renderState == 'EXPORT'">
            <p class="_p_export" style="order: 1;">
                The asset class is exported without reference foreign linking keys if
                exported via EXCEL.   JSON files retain all attributes.
                <br /><br/>
            </p>
            
            <br />
            <div class="_assets_file_controls" style="order: 2;">
                <!-- button class="_button_export_1"       type="button" @click="exportXLSX()">Save as Excel XLSX</button> -->
                <button class="_main_button" type="button" 
                    tabindex="0" 
                    aria-label="Save as Excel file" 
                    aria-describedby="Selects Excel file type for export" 
                    @click="exportXLSX()" 
                    @keydown.enter="exportXLSX()">Save as Excel</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Save as JSON file" 
                    aria-describedby="Selects JSON file type for export"
                    @click="exportJSON()"
                    @keydown.enter="exportJSON()">Save as JSON</button>  
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Cancel exports and return to list" 
                    aria-describedby="Cancels export"
                    @click="renderState = 'LIST'"  
                    @keydown.enter="renderState = 'LIST'">Cancel</button>  
            </div>    
        </div>

        <div class="_assets_controls">
            <br/>
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW' || renderState == 'EDIT'"
                tabindex="0" 
                aria-label="Return to asset list" 
                aria-describedby="Allows user to return to asset list" 
                @click="backToList()"
                @keydown.enter="backToList()">Back to List</button>

            <button class="_main_button" type="button"  v-if="(assets.length > 0) && (renderState == 'VIEW' || renderState == 'EDIT')"
                tabindex="0"
                aria-label="Next asset in list" 
                aria-describedby="Allows user to page thorugh assets in list order" 
                @click="nextAsset()"
                @keydown.enter="nextAsset()">Next </button>

            <button class="_main_button" type="button" v-if="renderState !='LIST' && renderState =='EDIT'"
                tabindex="0"
                aria-label="Cancel edits" 
                aria-description="Cancels current edit and returns asset to pre-edit state" 
                @click="cancelEdit()"
                @keydown.enter="cancelEdit()">Cancel </button>
            <button class="_main_button" type="button" v-if="renderState !='LIST' && renderState =='EDIT'"
                tabindex="0" 
                aria-label="Save edited asset" 
                aria-describedby="Saves the current asset" 
                @click="saveAsset()"
                @keydown.enter="saveAsset()">Save </button>
           <button class="_main_button" type="button" v-if="renderState =='DISPLAY-IMPORT-STATS' "
                tabindex="0" 
                aria-label="Exit statistics and render list" 
                aria-describedby="Exit and render asset list" 
                @click="exitStats()"
                @keydown.enter="exitStats()">Continue </button>

        </div>
        <!--   breaks to allow footer space -->
        <br/><br/>
    </div>

</template>

<script>
    import {ref, computed}          from 'vue'
    import {useStore}               from 'vuex'

    //import Component from '@/components/component.vue'
    import ViewAssetEntire          from '@/components/Assets/ViewAssetEntire.vue'
    import EditAsset                from '@/components/Assets/EditAsset.vue'
     
    //import composables from '@/composables/composable.js' 
    import useAssetsAPI             from '@/composables/useAssetsAPI'
    import useAuthAPI               from '@/composables/useAuthAPI'
  
    import useDateHelpers           from '@/composables/useDateHelpers'
    import useRefreshAPI            from '@/composables/useRefreshAPI'
    import usePermissionsAPI        from '@/composables/usePermissionsAPI'
    import useInferenceAPI          from '@/composables/useInferenceAPI'
    import * as XLSX                from 'xlsx-js-style'   
    import { SemipolarSpinner }     from 'epic-spinners'

    // import enumerations
    import SysConfigs               from '@/configuration/form-options.json'
    import Typology                 from '@/configuration/assetTypology.json'


    export default {
        components: {
            ViewAssetEntire,
            SemipolarSpinner,
            EditAsset
        },
        props: [
            'claims',
            'gat'
        ],
        emits: [
        ],
    
        setup(props)    {

            const { assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()
            const store = useStore()
            const { checkStoreForRefresh }  = useRefreshAPI()
            const { checkAdminGroups }      = usePermissionsAPI()

            //  store data
            const user                  =   computed( () => store.state.user)
                  
            const sysConfigs            =   ref([])
            sysConfigs.value            =   SysConfigs
            const typology              =   ref([])
            typology.value              =   Typology

            const units                 =   ref([])             // units list from config
            const unit                  =   ref({})             // selected unit
            const assets                =   ref([])
            const assetCount            =   ref(0)
            const currentSheet          =   ref("")             // import tag current org
            const asset                 =   ref({})
            const backupAsset           =   ref({})
            const aExportableAssets     =   ref([])
            const aRawImportAssets      =   ref([])
            const aAssetTypology        =   ref([])             // configuration typology for assets
            const bAssetsLoaded         =   ref(false)          // takes a long! time to pull in 15,000 assets

            const stats                 =   ref({
                'sheetsProcessed':          0,
                'startTimestamp':           0,
                'elapsedImportTime':        "",
                'inputRecords':             0,
                'badOrgcodeRecords':        0,
                'badItemIDLengthRecords':   0,
                'validSourceRecords':       0,
                'unresolvedMfgInputs':      0,
                'unresolvedModelInput':     0,
                'resolvedModelInference':   0,
                'modelInputEmpty':          0,
                'noCustodiansListed':       0,
                'custodiansNotInAD':        0,
                'notListedTypeRecords':     0,
                'inferenceSuccessRate':     0.0,
                'modelEvalSuccessRate':     0.0,
                'finRecords':               0
            })

            // pull units from unit typology in configuration/form-options
            // Structure:        //      'unit'
            //      'unit-id'
            //      'short-name'
            //      'listed-stewards'  // FABWeb+ listed stewards for unit

            units.value = typology.value["units"]

            // asset - typology characteristics from configuration form-options
            // Structure of JSON typology record:
            //      type:           listable type of asset
            //      ci:             Boolean for configuration item
            //      wipeable:       Boolean for whether asset has disk/storage that requires wiping on surplus
            //      dep-category:   Depreciation category (property useful period)
            //      audit-cat:      State of Illinois audit category
            //      aliases[]:      Array of names used to identify type from description (typically on imports)

            aAssetTypology.value    =   typology['asset-typology']

            const viewIndex         =   ref(0)
            const orgDisplay        =   ref(null)
            const processSelector   =   ref(null)
            const selectedFilename  =   ref("")

            const filters           =   ref({
                'steward':              "",
                'steward-netid':        "",
                'owner-org':            "",
                'asset-type':            "",
                'tag':                  "",
                'serial-no':            "",
                'audit-category':       "",
                'date-last-audit':      "",
                'mfg':                  "",
                'model':                "",
                'machine-name':         "",
                'os-type':              "",
                'os-version':           "",
                'description':          "",
                'storage-location':     "",
                'source-code':          "",
                'en-disk_encrypted':    false,
                'disk-encrypted':       null,
                'en-crowdstrike-active': false,
                'crowdstrike-active':   null,
                'en-firewall-active':   false,
                'firewall-active':      null,
                'ipv4-address':         "",
                'en-av-installed':      false,
                'av-installed':         null,
                'av-name':              "",
                'av-version':           "",
                'en-has-sensitive-data': false,
                'has-sensitive-data':   null,
                'data-class':           "",
                'en-user-is-admin':     false,
                'chart':                "Urbana",           
                'cost':                 null,    
                'category':             "",      
                'otag':                 "", 
                'location':             "", 
                'location-detail':      "",
                'invoice-number':       "",
                'it-data':              "",
                'notes':                "",
                'orig-item-id':         "", 
                'purchase-date':        "",
                'replacement-cycle':    "",
                'status':               "",      
                'unit':                 "",
                'warranty-expiration':  "",
                'user-is-admin':        null
            })

            let aOrgs = Array.prototype.slice.call(user.value['allowed-orgs'])
            orgDisplay.value = user.value['allowed-orgs'].toString()

            if(user.value['is-sys-admin']) {
                //await fetchAssetsOnQuery({})
                fetchAssetsOnQuery({})
                .then( () => {
                    assets.value = assetResults.value 
                    return assets.value.length
                })
                .then( () => {
                    assetCount.value = assets.value.length
                    bAssetsLoaded.value = true
                })
            }
            else {
                //await fetchAssetsOnQuery({})
                fetchAssetsOnOrgs(aOrgs)
                .then( () => {
                    assets.value = assetResults.value 
                    return assets.value.length
                })
                .then( () => {
                    assetCount.value = assets.value.length
                    bAssetsLoaded.value = true
                })
            }
                
            checkAdminGroups(props.claims)

            const filteredAssets = computed( () => {

                let aFilteredValues = assets.value
                // since we use '*' to represent a input value that was not inferenced, filternig on '*'
                // must be escaped (double \\)prior to avoid javscript regex error

                aFilteredValues = aFilteredValues.filter( (asset) => {
                    return asset['tag'].toLowerCase().match(filters.value['tag'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['asset-type'].toLowerCase().match(filters.value['asset-type'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['owner-org'].toLowerCase().match(filters.value['owner-org'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['steward'].toLowerCase().match(filters.value['steward'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['steward-netid'].toLowerCase().match(filters.value['steward-netid'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['status'].toLowerCase().match(filters.value['status'].toLowerCase().replace("*","\\*"))
                })               
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['serial-no'].toLowerCase().match(filters.value['serial-no'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['description'].toLowerCase().match(filters.value['description'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['mfg'].toLowerCase().match(filters.value['mfg'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['model'].toLowerCase().match(filters.value['model'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['location'].match(filters.value['location'].replace("*","\\*"))
                })
                return aFilteredValues
            })

            const prettySelectedFilename = ref("")

            return {user, units, unit, assets, asset, aExportableAssets, aRawImportAssets, backupAsset, sysConfigs, 
                    viewIndex, orgDisplay, filters, filteredAssets, processSelector, selectedFilename, aAssetTypology,
                    bAssetsLoaded, assetCount, currentSheet, stats, prettySelectedFilename }
        },

        data() {

            return {
                renderState:        'LIST',  
                savedFlag:          false,
                filepath:           "",
                filename:           "",
                datafile:           "",
                bFileTypeError:     false,
                bIncludeArrayProps: false,
                bDataAvail:         false 
            }
        },
        methods: {
            // FUNCTION:  cleans up ISO date string
            prettyDate(date)     {
                const {makePrettyDate}      =   useDateHelpers()
                return makePrettyDate(date)  
            },
            // Function: converts status enumeration to more readable 
            prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            },
            // FUNCTION to save an asset to database
            saveAsset() {

                const {assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()

                storeAsset(this.filteredAssets[this.viewIndex])
                .then(async () => {
                    await fetchAssetsOnQuery({id: assetKeyData.value[0].pkey})
                    .then( () => {
                        this.filteredAssets[this.viewIndex]  = assetResults.value[0]    

                        // since asset is reactive to assets[idx] should be updated
                        this.renderState        =   'EDIT'
                        this.savedFlag          =   true
                    
                    })
                })    
            },
            cancelEdit()    {
                this.filteredAssets[this.viewIndex] = this.backupAsset
                this.renderState = 'LIST'
            },
            viewAsset(_,idx)     {
                this.viewIndex = idx
                this.renderState = 'VIEW'
            },
            editAsset(_,idx)     {

                // set up backup for cancels
                this.backupAsset = JSON.parse(JSON.stringify(this.filteredAssets[this.viewIndex]))

                this.viewIndex = idx
                this.renderState = 'EDIT'
            },
            backToList() {
                this.renderState    =   "LIST"  
                this.savedFlag      =   false  
            },
            // FUNCTION: increments through viewing index (experimental)
            nextAsset() {
                this.viewIndex++
                if (this.viewIndex >= this.filteredAssets.length) this.viewIndex = 0
                this.savedFlag      =   false  
            },
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            // Data Import Processes
            // Button triggers render switch to IMPORT rendering
            // Option buttons are presented
            // User clicking option button will request file name for files 
            // For file imports, a file type state is set and the 'FILE' page is rendered.
            // Specified file is accessed and processing in switched on processSelector 
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 

            selectFabWebPlusFile() {
                this.processSelector = 'FABWEB+'
                this.renderState = 'FILE'
            },
            selectFabWebFile() {
                this.processSelector = 'FABWEB'
                this.renderState = 'FILE'
            },
            selectOtherFile() {
                // determine process selector from file type
                this.processSelector = 'OTHER'
                this.renderState = 'FILE'
            },
            // Function is triggered on file name submit on rendered page
            async importFileSelection(event) {
 
                this.renderState = 'READ-FABWEB-DATA'

                // carve up the browser supplied string
                this.selectedFilename = event.target.value
                
                let mimeType = event.target.files[0].type
                let aParts = event.target.files[0].name.split(/[\\/]/)
                let sFilenameWithExtension = aParts.pop()
                let filenameParts = sFilenameWithExtension.split('.');

                const filename = filenameParts.slice(0, -1).join('.'); // Handles filenames with multiple dots
                const filetype = filenameParts.length > 1 ? filenameParts.pop() : '';
                //console.log("ListAssets:: importFileSelection: specified filename: "+filename+", type: "+filetype)
                
                const FileInput = document.querySelector("input[type=file]")
                switch(filetype) {
            
                        case "json":
                            FileInput.addEventListener("change", this.importJSON(FileInput))
                            break;

                        case "csv":
                            FileInput.addEventListener("change", this.importCSV(FileInput))
                            break;

                        case "xlsx":
                            FileInput.addEventListener("change", this.importXLSX(FileInput))
                            break;
                        
                    default:
                        this.bFileTypeError = true
                        this.renderState = 'FILE'
                    break;
                }
            },
            selectJSONFile() {
                this.processSelector = 'JSON'
                this.renderState = 'FILE'
            },
            selectCSVFile()    {
                this.processSelector = 'CSV'
                this.renderState = 'FILE'
            },
            selectXLSXFile() {
                this.processSelector = 'XLSX'
                this.renderState = 'FILE'
            },
            async importJSON(fileInput)    {

                const { assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()

                

                // back to the barn
                this.renderState = 'LIST'
                
            }, 
            // Import may be XLSX from this app, FABWeb+ XLSX import, or FABWeb XLSX import
            async importXLSX(fileInput) {
 
                this.prettySelectedFilename = fileInput.files[0].name

                console.log("XLSX Import: "+  this.prettySelectedFilename)
                this.stats.startTimestamp = performance.now()

                const { assetResults, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()
                const { assetProps, fixupAssetProps, bBadFile } = useInferenceAPI()

                const [file] = fileInput.files
                var rawFWData = ""
                if (file) {
                    // since we are loading, and not on a display page, set to false
                    this.bDataAvail = false

                    rawFWData = await file.arrayBuffer()
                    const workbook =  XLSX.read(rawFWData,{cellDates: true})

                    // MARK - MANIPULATE FABWEB ASSETS INTO THIS DB ASSETS Sheet By Sheet
                    // console.log("ListAssets:: importXLSX: workbook: "+JSON.stringify(workbook))     // maybe
                    // iterate over worksheets and makes one store call per worksheet
                    
                    //console.log("ListAssets:: importXLSX: workbook.sheetNames: "+JSON.stringify(workbook.SheetNames)) 
                    //workbook.SheetNames.forEach( async (_, sidx) => {
                    for( const[sidx, sht] of workbook.SheetNames.entries()) {
                        // clear any residual data 
                        var rawAAData = []
                        // load raw data from sheet into AofA
                        this.stats.sheetsProcessed++

                        // DEBUG
                        //console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>")
                        console.log("listAssets:: importXLSX: Working on sheet: "+workbook.SheetNames[sidx])
                        this.currentSheet = workbook.SheetNames[sidx]
                        rawAAData = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[sidx]],{defval:"", 
                        blankrows: false, header: 1})

                        // DEBUG
                        //console.log("ListAssets:: importXLSX: rawAAData: "+JSON.stringify(rawAAData))
                        await fixupAssetProps ( rawAAData, this.processSelector, this.stats) 
                        .then( async () => {
                            
                            if(!bBadFile.value) {
                                // DEBUG
                                //console.log("ListAssets:: importXLSX: Number of assetProps returned to store: "+assetProps.value.length)
                                //console.log("Assets: "+JSON.stringify(assetProps.value))
                                await storeAsset( assetProps.value)
                           
                            } 
                            else {
                                // column headers not in correct fortmat
                                console.log("ListAssets:: importXLSX: Sheet named "+workbook.SheetNames[sidx]+" in bad format. Skipping")
                                this.bDataAvail = false
                                this.renderState = 'BAD-FABWEB-IMPORT'
                            }      
                        })                                  
                    }

                    // all sheets stored - reload for table use       
                    if(this.user['is-sys-admin']) {
                        await fetchAssetsOnQuery({})
                        .then( () => {
                            this.assets = assetResults.value 
                            this.assetCount = this.assets.length
                            this.renderState = 'DISPLAY-IMPORT-STATS'    
                            this.bDataAvail = true
                            // determine import elapsed time
                            this.computeElapsedTime()
                            this.computeSuccessRate()
                        })
                       
                    }
                    else {
                        await fetchAssetsOnOrgs(aOrgs)
                        .then( () => {
                            this.assets = assetResults.value 
                            this.assetCount = this.assets.length
                            this.renderState = 'DISPLAY-IMPORT-STATS'    
                            this.bDataAvail = true
                            // determine import elapsed time
                            this.computeElapsedTime()
                            this.computeSuccessRate()
                        })     
                    }
                }   
            },
            async importCSV(fileImport) {
                console.log("LE:: importCSV triggered")

                const { assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()


                // back to the barn
                this.renderState = 'LIST'
            },
            exitStats() {
                this.renderState = 'LIST'
            },
            computeElapsedTime()    {
                // elapsed time in milliseconds
                let elapsedTime         =   performance.now() - this.stats.startTimestamp
 
                let elapsedHours        =   Math.floor(elapsedTime/(1000*60*60)) 
                let residualHours       =   elapsedTime%(1000*60*60)     
                let elapsedMinutes      =   Math.floor(residualHours/(1000*60))
                let residualMinutes     =   residualHours%(1000*60)  
                let elapsedSeconds      =   Math.floor(residualMinutes/1000) 
                let elapsedMilliSec     =   residualMinutes%1000        
                // generate a readable string
                this.stats.elapsedImportTime = ""
                if(elapsedHours > 0)    this.stats.elapsedImportTime    += elapsedHours+" hours  "
                if(elapsedMinutes > 0)  this.stats.elapsedImportTime    += elapsedMinutes+" min.  "
                if(elapsedSeconds > 0)  this.stats.elapsedImportTime    += elapsedSeconds+" sec.  "
                if(elapsedMilliSec > 0) this.stats.elapsedImportTime    += elapsedMilliSec+" ms."

            },
            computeSuccessRate() {
                // the universe of viable records is input - records with bad ordcodes
                // bad item-ids are truncated and processed, and therefore not removed
                this.stats.validSourceRecords = this.stats.inputRecords - this.stats.badOrgcodeRecords - this.stats.finRecords

                // MEASURE MODEL INFERENCE EFFICIENCY
                // sourceRecords with valid model label (not blank, N/A) 
                let modelSource = this.stats.validSourceRecords - this.stats.modelInputEmpty
                this.stats.modelEvalSuccessRate = (this.stats.resolvedModelInference/modelSource) * 100.0
                
                // MEASURE OVERALL PERFORMANCE        
                this.stats.inferenceSuccessRate = ((this.stats.validSourceRecords - this.stats.notListedTypeRecords)/this.stats.validSourceRecords) * 100.0

            },

            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            // Data Export Processes
            // Button triggers render switch to EXPORT rendering
            // Option buttons are presented
            // User clicking option button will generate file and download to braowser 'downloads'
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 
            exportAssets()  {
            
                this.renderState = 'EXPORT'
            },
            exportXLSX()    {
                // check for empty filteredlist
                if(this.filteredAssets.length == 0)  {
                    this.renderState = 'LIST'
                    return
                }
                const workbook  =   XLSX.utils.book_new()

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          SECTION TO GENERATE SPREADSHEET CELL HEADER VALUES
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                // header generator Booleans - will be set true when found, otherwise will be excluded
                // from set reporting
                let bHaveAssetHeaders   = false
               
                // set up headers - pick up the first asset [0] and strip headers
                //
                // iterate the assets to extract headers
                let aAssetHeaders    =   []      // array of header keys
                 
                this.filteredAssets.forEach( (a) =>    {
                    if(!bHaveAssetHeaders) {
                        aAssetHeaders = Object.keys(a ?? {}) 
                        if(aAssetHeaders.length > 2 )   {
                            bHaveAssetHeaders = true
                        }
                    }
                })
                            
                // MARK: - Remove database specific FKs
                let propIndex = 0
                propIndex = aAssetHeaders.indexOf('fk-employee-id')
                if(propIndex != -1) aAssetHeaders.splice(propIndex,1)
                propIndex = aAssetHeaders.indexOf('fk-agreement-id')
                if(propIndex != -1) aAssetHeaders.splice(propIndex,1)
                                  
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          MAKE META RECORDS AT TOP OF SPREADSHEET
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                var currentSheet    =   []             //   array or rows
                var currentRow      =   []             //   array of cells
                let cRow            =   0              //   indexed to 0 in 'xlsx-js-style'
                let hdrRows         =   []             // emphasized rows (headers)
                let boldRows        =   []             // bolded data rows for agreement data (easier to differentiate)
                let sectionHeads    =   []             // + components

                // generate date of report
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                currentRow.push("Exported Assets")
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push(" ")
                currentRow = []
                currentRow.push("Date:")
                currentRow.push(month+'/'+day+'/'+year)
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push("Time:")
                let time = (hours >= 12)?(hours-12+":"+mins+" PM"):(hours+":"+mins+" AM")
                currentRow.push(time)
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push("Created by:")
                currentRow.push(this.user['name-concat'])
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push(" ")
                currentSheet.push(currentRow); cRow++
                currentSheet.push(currentRow); cRow++
                // end of meta data
                
                // create asset header row
                currentRow = []
                aAssetHeaders.forEach( (hdr) => {
                    currentRow.push(hdr)
                })
                // bold the row following (pre-increment cRow)
                hdrRows.push(cRow)
                currentSheet.push(currentRow); cRow++


                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          ITERATE FILTERED ASSETS CONVERTING ASSETS TO SHEET ROWS
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                currentRow = []
                this.filteredAssets.forEach( (_,i) => {
                    aAssetHeaders.forEach((hdr) =>    {
                        // fixup dates
                        if(hdr == 'purchase-date' || hdr == 'date-last-audit' || hdr == 'disk-wiped-date' ) {
                            let rawDate = this.filteredAssets[i]?.[hdr]
                            if(rawDate) {
                                //let dt = new Date(rawDate.substring(0,10)) 
                                let dt = new Date(rawDate) 
                                currentRow.push(dt.toLocaleDateString())
                            }
                            else currentRow.push(this.filteredAssets[i]?.[hdr])
                        }
                        else if(hdr == 'cost') {
                            currentRow.push(this.filteredAssets[i]?.[hdr].toFixed(2))
                        }
                        else currentRow.push(this.filteredAssets[i]?.[hdr])
                        
                    })
                    currentSheet.push(currentRow); cRow++
                    currentRow = []

                    // continue iterating assets
                })

                // build the workbook with current data    
                var worksheet = XLSX.utils.aoa_to_sheet(currentSheet)

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //  
                //          APPLY FORMATTING TO SHEET
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                // format primary 'Employees' tab
                // !cols is an array of column format data used in xlsx-js-style - presumed objects {}
                // the maximum number of columns is the maximum length of the length of the headers array
                const sheetColumns = aAssetHeaders.length
                // maximum column width is established because of text entries in definitions.  Data must be
                // folded.  Minimum width provides for spacing for " " entries
                const maxWidth = 40
                const minWidth = 5

                // initialize style array to minWidth
                let aColStyles = []
                for (let i = 0; i < sheetColumns; i++) {
                    let oColArg    =    {
                        wch: minWidth
                    }
                    aColStyles.push(oColArg)
                }
                
                // iterate on array determined the maximum width of a column for cRow rows.
                for (let i = 0; i < cRow; i++)  {
                    // how many elements in the ith row array of the sheet array
                    if(currentSheet?.[i]?.length >= 1)   {
                        // iterate over the elements measuring the length (width precursor)
                        for (let j = 0; j < currentSheet?.[i]?.length; j++)  {
                            let element = currentSheet?.[i]?.[j]
                            // if null record coalesce to default width
                            let elWidth = element?.length ?? minWidth
                            // restrict cell to maximum width
                            elWidth = (elWidth > maxWidth)?maxWidth:elWidth
                            aColStyles[j].wch = Math.max(elWidth, aColStyles[j].wch)  
                        }
                    }
                }

                // MARK: Apply format of column width
                worksheet["!cols"] = aColStyles

                // run cell styler
                for (let i in worksheet) {
                    if (typeof(worksheet[i]) != "object") continue;
                    let cell = XLSX.utils.decode_cell(i);

                    // MARK: set global styles
                    worksheet[i].s = {
                        font:   {
                            sz:     "10",
                            color:  {rgb: "000000"}
                        },
                        alignment:  {
                            horizontal:     "left",
                            wrapText:       true
                        }
                    }
                    if(cell.r == 0) {
                        // first row
                        worksheet[i].s = {
                            font:   {
                                sz:     "12",
                                color:  {rgb: "0000FF"},
                                bold: true
                            },
                        }
                    }
                    if(hdrRows.includes(cell.r))   {
                        // header rows highlighted
                        worksheet[i].s = {
                            //fill: {
                            //    patternType:    "solid",
                            //    bgColor:        {rgb: "090909"}      
                            //},
                            font: {
                                sz:             "11",
                                bold:           true,
                                color:          {rgb: "0000FF"}
                            }
                        }
                    }
                    if(boldRows.includes(cell.r))   {
                        // asset data bolded
                        worksheet[i].s = {
                            font: {
                                bold:           true
                            }
                        }
                    }
                    if(sectionHeads.includes(cell.r)) {
                        worksheet[i].s = {
                            font:   {
                                sz:     "11",
                                color:  {rgb: "999900"},
                                bold: true
                            }
                        }
                    }
                }
 
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          CONSTRUCT THE XLSX WORKBOOK 
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Assets');

                // 20240404_1559 format
                const filedate = year+month+day+'_'+hours+mins
                this.filename = "assets"+"-"+filedate+".xlsx"    

                XLSX.writeFile(workbook, this.filename, { compression: true, cellStyles: true });

                this.renderState = 'LIST'
                return
            },            
            exportJSON() {

                // check for empty filteredlist
                if(this.filteredAssets.length == 0)  {
                    this.renderState = 'LIST'
                    return
                }
                // convert employees to JSON datafile
                this.datafile = JSON.stringify(this.filteredAssets)

                var blobData = []
                blobData.push(this.datafile)

                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                // 20240404_1559 format
                const filedate = year+month+day+'_'+hours+mins
                this.filename = "assets"+"-"+filedate+".json"    

                var textFileAsBlob =  new Blob(blobData, {type:'application/json'});
                var downloadLink = document.createElement("a")
                downloadLink.setAttribute("id","_pseudoID")
                
                downloadLink.download = this.filename
                downloadLink.innerHTML = "_"
                if (window.webkitURL != null) {
                    // Chrome allows the link to be clicked
                    // without actually adding it to the DOM.
                    downloadLink.href = window.webkitURL.createObjectURL(textFileAsBlob)
                }
                else  {
                    // Firefox requires the link to be added to the DOM
                    // before it can be clicked.
                    downloadLink.href = window.URL.createObjectURL(textFileAsBlob)
                    downloadLink.onclick = document.body.removeChild(target)
                    downloadLink.style.display = "none"
                    document.body.appendChild(downloadLink)
                }
                downloadLink.click()
                window.URL.revokeObjectURL(textFileAsBlob)
                downloadLink.remove()

                // back to the barn
                this.renderState = 'LIST'
            },
 
            setFilters(s)    {
                // placeholder
            },
            clearFilters() {
                this.filters = {
                    'steward':              "",
                    'steward-netid':        "",
                    'owner-org':            "",
                    'asset-type':           "",
                    'tag':                  "",
                    'serial-no':            "",
                    'audit-category':       "",
                    'date-last-audit':      "",
                    'mfg':                  "",
                    'model':                "",
                    'machine-name':         "",
                    'os-type':              "",
                    'os-version':           "",
                    'description':          "",
                    'storage-location':     "",
                    'source-code':          "",
                    'disk-encrypted':       null,
                    'crowdstrike-active':   null,
                    'firewall-active':      null,
                    'ipv4-address':         "",
                    'av-installed':         null,
                    'av-name':              "",
                    'av-version':           "",
                    'has-sensitive-data':   null,
                    'data-class':           "",
                    'chart':                "Urbana",           
                    'cost':                 null,    
                    'category':             "",      
                    'otag':                 "", 
                    'location':             "", 
                    'location-detail':      "",
                    'invoice-number':       "",
                    'it-data':              "",
                    'notes':                "",
                    'orig-item-id':         "", 
                    'purchase-date':        "",
                    'replacement-cycle':    "",
                    'status':               "",      
                    'unit':                 "",
                    'warranty-expiration':  "",
                    'user-is-admin':        null
                }
            }
       
        },


        // hooks
        beforeMount() {

        },
        mounted() {
           
        },
        updated() {

        }
    }
</script>

<style scoped>
    ._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50vh;
        margin: 25px 0 0;
        padding: 0;

    }
    ._spinner {
        min-height: 40vh;
        width: 40vw;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 45px auto;
        text-align: center;
    }
    ._list_container {
        width: 98%;
        overflow-x: auto;
        overflow-y: auto;
        margin: 0 5px;;
        padding: 0 5px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: white;
    }
    ._export_container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        overflow-x: auto;
        margin: 0;
        padding: 0 5px;
    }
    ._list {
        margin: 0;
        padding: 0;
    }
    ._p_title_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 10pt;
        font-weight: bold;
        text-align: center;
    }
    ._p_list_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 10pt;
        font-weight: bold;
        text-align: left;
    }

    ._p_list_actions_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 10pt;
        font-weight: bold;
        text-align: center;
    }
    ._p_export {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_import {
        width: 50%;
        margin: 25px auto 0;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_import_center {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: center;
    }
    ._file_entry {
        display: flex;
        flex-direction: row;
        align-content: flex-start;
        width: 50%;
        margin: 0 auto;
    }
    ._file_selector {
        width: 100%;
        margin: 5px auto;
        text-align: left;
        background-color: #BFC9D9;
        border-style: solid;
        border-color: #13294B;
        border-width: 2px;
        border-radius: 4px;
        color: black;
        font-size: 1.0em;
        font-weight: normal;
    }
    input[type="file"]::file-selector-button {
        color: #ccc;
        border-width: 2px;
        border-radius: 4px;
        border-color: black;
        background-color: #13294B;
        margin: 2px 15px 2px 35px;
        padding: 1px;
    }
    input[type="file"]::file-selector-button:hover {
           background-color: #FF5F05;
    }



    /* in-list action button styling */
    ._action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_clear_filters {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._button_clear_filters:hover {
        background-color: #FF5F05;
        color: white;
    } 
    ._assets_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
    }
    ._assets_file_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 25px 0 0 0;
        padding: 0 5px;
    }
    ._scrollable_list_container {
        width: 100%;
        height: 500px;
        overflow: scroll;
        margin: 0;
        padding: 0;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: white;
    }

    ._assets_table {
        display: table;
    }
    ._stat_table {
        display: table;
        width: 60%;
        margin: 0 auto;
        border-style: solid;
        border-width: 4px;
        border-color: #13294B;
        background-color: white;
        border-collapse: collapse;

    }
    ._assets_table_fixed_row   {
        display: table-row;
        position: sticky !important;
        background-color:#BFC9D9;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: #13294B;
        height: 25px;
        white-space: nowrap;
        z-index: 1;
    }
    ._assets_table_row   {
        background-color:#a4b9dc;        
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        height: 25px;
        white-space: nowrap;
    }
    ._stat_table_row   {
        background-color:#a4b9dc;        
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        height: 25px;
        white-space: nowrap;
    }
    ._stat_table_row_bordered   {
        background-color:#a4b9dc;        
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        height: 25px;
        white-space: nowrap;
        border-bottom: 1px solid #13294B;
    }
    ._stat_table_cell  {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 2px 0;
        padding: 3px 5px 3px 30px;
    }
    ._hr_full_row {
        display: table-cell;
        width: 100%;
    }
    ._assets_table_row:nth-child(2n+2) {
        background-color:#BFC9D9;
    }
    ._assets_table_header_cell {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 0;
    }
    ._assets_table_cell  {
        display: table-cell;
        min-width: 15%;
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 30px;
    }
    ._assets_table_cell_ellipsis {
        /* display: table-cell; */
        max-width: 80%;
        text-overflow: ellipsis;
        white-space: nowrap;        
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 10px;
        
    }
    ._asset_actions_table_cell {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 0 auto;
    }
    ._no_assets_warning_container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 20px;
        margin: 10px 0;
    }
    ._assets_warning {
        color: #FF5F05;
    }
    _._assets_warning_clear {
        float: right;
    }  
    ._input_filter {
        width: 70%;
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 1px;
        border-color: #13294B;
        margin: 0 3px 0;
        padding: 2px;
    }
    
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }

    ._p_patience {
        width: 30%;
        font-size: 1em;
        color: #13294B;
        text-align: center;
        padding: 2px;
        margin: 2px auto;
    }
    ._p_saved {
        font-size: 14pt;
        color: #FF5F05;
        padding: 2px;
    }
    ._saved_right {
        padding: 0 25px;
        margin: 0 50px 0 0;
        float: right;
    }
    ._assets_table_link_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        font-weight: normal;
        color: #13294B;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._assets_table_link_cell:hover  {
        font-weight: bold;
        color: #FF5F05;
    }
    ._ul_instructions {
        width: 50%;
        color:#006230;
        font-size: 0.9em;
        font-weight: normal;
        margin: 0 auto;
        padding: 2px;
    }
    ._li_instructions {
        width: 50%;
        color:#006230;
        font-size: 0.9em;
        font-weight: bold;
        margin: 0 auto;
        padding: 2px;
    }
    ._input {
        width: 25%;
        padding: 2px 5px;
        border: 2px solid #ccc;
        border-color: #13294B;
        color: black;
        border-radius: 4px;
        box-sizing: border-box;
    }
    ._label {
        display: inline-block;
        color: #13294B;
        font-weight: bold;
        font-size: 0.95em;
        text-align: left;
        padding: 2px 0;  
    }
    ._col_50 {
        width: 50%;
        margin-top: 2px;
        text-align: left;
    }
  

</style>